import Calendar from '@fullcalendar/react'; // => request placed at the top
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';
//
import { useCallback, useEffect, useMemo, useState } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Container from '@mui/material/Container';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Stack } from '@mui/system';
import { Avatar, Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { setError } from 'src/redux/slices/error';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// _mock
import { CALENDAR_COLOR_OPTIONS } from 'src/_mock/_calendar';
// api
import { updateEvent } from 'src/api/calendar';
// components
import { DateTime } from 'luxon';
import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';
// types
import { ICalendarEvent, ICalendarFilters, ICalendarFilterValue } from 'src/types/calendar';
import { ShiftDto, ShiftsService } from 'src/api';
//
import { EventClickArg, EventContentArg, EventDropArg } from '@fullcalendar/core';
import { StyledCalendar } from '../styles';
import CalendarShiftForm from '../calendar-shift-form';
import CalendarToolbar from '../calendar-toolbar';
import CalendarFiltersResult from '../calendar-filters-result';
import { useLocales } from '../../../locales';
import { paths } from '../../../routes/paths';
import { RouterLink } from '../../../routes/components';
import CustomBreadcrumbs from '../../../components/custom-breadcrumbs';
import { useDispatch, useSelector } from '../../../redux/store';
import { useCalendar, useEvent } from '../../calendar/hooks';
import { getShifts } from '../../../redux/slices/shift';

// ----------------------------------------------------------------------

const defaultFilters: ICalendarFilters = {
  colors: [],
  startDate: null,
  endDate: null,
};

// ----------------------------------------------------------------------

export default function ClassesListView() {
  const theme = useTheme();

  const settings = useSettingsContext();

  const { t } = useLocales();

  const smUp = useResponsive('up', 'sm');

  const dispatch = useDispatch();

  const selectedGym = useSelector((state) => state.gym.globalGym);

  const [filters, setFilters] = useState(defaultFilters);

  const shifts = useSelector((state) => state.shift.shifts);

  const eventsLoading = useSelector((state) => state.shift.isLoading);

  const events = useMemo(
    () =>
      shifts.map((e) => {
        const event = {
          id: `${e.id}`,
          color: e.tint ?? '',
          title: e.employeeName ?? '',
          description: e.description ?? '',
          end: e.ends?.toString(),
          start: e.begins?.toString(),
        } as ICalendarEvent;

        return {
          ...event,
          textColor: e.tint ?? '',
          shift: e,
        };
      }),
    [shifts]
  );

  const storedDate = useMemo(() => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get('date') ? decodeURIComponent(queryParams.get('date')!) : null;
  }, []);

  const {
    calendarRef,
    //
    view,
    date,
    startDate,
    endDate,
    //
    onDatePrev,
    onDateNext,
    onDateToday,
    onChangeView,
    onSelectRange,
    onResizeEvent,
    //
    openForm,
    onOpenForm,
    onCloseForm,
    //
    selectEventId,
    setSelectEventId,
    selectedRange,
  } = useCalendar({
    defaultDate:
      storedDate !== null
        ? DateTime.fromISO(storedDate, {
            zone: selectedGym.timeZone,
          })
        : undefined,
  });

  const currentEvent = useEvent(events, selectEventId, selectedRange, openForm);

  const currentShift = useMemo(
    () => shifts.find((x) => `${x.id}` === currentEvent?.id),
    [currentEvent, shifts]
  );

  useEffect(() => {
    dispatch(
      getShifts({
        fromDate: startDate,
        toDate: endDate,
        pageSize: 2 ** 31 - 1,
        pageNumber: 0,
      })
    );
  }, [dispatch, startDate, endDate, selectedGym]);

  useEffect(() => {
    const newurl =
      `${window.location.protocol}//${window.location.host}${window.location.pathname}` +
      `?date=${date ? encodeURIComponent(date.toISO({ includeOffset: true })!) : ''}`;
    window.history.replaceState({ path: newurl }, '', newurl);
  }, [date]);

  const handleFilters = useCallback((name: string, value: ICalendarFilterValue) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const canReset = !!filters.colors.length || (!!filters.startDate && !!filters.endDate);

  const renderResults = (
    <CalendarFiltersResult
      filters={filters}
      onFilters={handleFilters}
      //
      canReset={canReset}
      onResetFilters={handleResetFilters}
      //
      results={events.length}
      sx={{ mb: { xs: 3, md: 5 } }}
    />
  );

  const onClickEvent = useCallback(
    (arg: EventClickArg) => {
      const { event } = arg;
      onOpenForm();
      setSelectEventId(event.id);
    },
    [onOpenForm, setSelectEventId]
  );

  const onDropEvent = useCallback(
    async (arg: EventDropArg) => {
      const { event } = arg;

      try {
        // Check if is edit mode
        const shift = shifts.find((x) => `${x.id}` === event?.id);
        if (shift) {
          await ShiftsService.update({
            id: shift!.id!,
            body: {
              id: shift!.id!,
              description: shift?.description || '',
              staffMemberId: shift?.employeeId || '',
              numberOfRepetitions: '',
              gymId: shift?.gymId || selectedGym?.id,
              wageRateId: shift?.wageRateId,
              begins: DateTime.fromJSDate(event.start!)
                .toUTC()
                .setZone(selectedGym.timeZone, { keepLocalTime: true }),
              ends: DateTime.fromJSDate(event.end!)
                .toUTC()
                .setZone(selectedGym.timeZone, { keepLocalTime: true }),
            } as any,
          });

          enqueueSnackbar(shift ? t(`Update success!`) : t('Create success!'));

          dispatch(
            getShifts({
              fromDate: startDate,
              toDate: endDate,
              pageSize: 2 ** 31 - 1,
              pageNumber: 0,
            })
          );
        }
      } catch (error) {
        dispatch(setError(error));
      }
    },
    [shifts, dispatch, t, endDate, startDate, selectedGym]
  );

  const { currentLang } = useLocales();

  const renderEventContent = (arg: EventContentArg) => {
    const extendedProps = arg.event.extendedProps as any;
    const clazz = extendedProps.shift as ShiftDto;
    return (
      <Stack sx={{ position: 'relative' }}>
        <Box
          className="fc-event-main-frame"
          sx={{
            flexDirection: 'column',
          }}
        >
          <div
            className="fc-event-time"
            style={{
              maxHeight: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {clazz.begins?.toFormat('HH:mm')} - {clazz.ends?.toFormat('HH:mm')}
          </div>
          <Box
            className="fc-event-title-container"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <Box
              className="fc-event-title"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                height: '20px',
              }}
            >
              {clazz.description}
            </Box>
            <Box
              className="fc-event-title"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                height: '20px',
              }}
            >
              {clazz.employeeName}
            </Box>
          </Box>
        </Box>
        <Tooltip title={clazz.employeeName}>
          <Avatar
            sx={{
              width: 24,
              height: 24,
              right: 0,
              top: 2,
              position: 'absolute',
            }}
            src={clazz.employeeProfileImageUrl}
            alt={clazz.employeeName}
          />
        </Tooltip>
      </Stack>
    );
  };

  return (
    <>
      <Container maxWidth={settings.themeStretch ? false : 'xl'}>
        <CustomBreadcrumbs
          heading={t('Scheduled shifts')}
          links={[
            { name: t('Dashboard'), href: paths.dashboard.root },
            {
              name: t('Duty Schedule'),
              href: paths.dutySchedule.root,
            },
            { name: t('Calendar') },
          ]}
          action={
            <Button
              component={RouterLink}
              href={paths.classTemplates.new}
              variant="contained"
              color="primary"
              startIcon={<Iconify icon="mingcute:add-line" />}
            >
              {t('Scheduling')}
            </Button>
          }
          sx={{ mb: { xs: 3, md: 5 } }}
        />

        {canReset && renderResults}

        <Card>
          <StyledCalendar>
            <CalendarToolbar
              date={date.toJSDate()}
              view={view}
              loading={eventsLoading}
              onNextDate={onDateNext}
              onPrevDate={onDatePrev}
              onToday={onDateToday}
              onChangeView={onChangeView}
            />

            <Calendar
              weekends
              editable
              droppable
              selectable
              rerenderDelay={10}
              allDayMaintainDuration
              ref={calendarRef}
              initialDate={date.toJSDate()}
              initialView={view}
              dayMaxEventRows={3}
              eventDisplay="block"
              events={events}
              locale={currentLang.value}
              slotDuration="00:15:00"
              headerToolbar={false}
              select={onSelectRange}
              eventClick={onClickEvent}
              eventContent={renderEventContent}
              height={smUp ? 720 : 'auto'}
              eventDrop={onDropEvent}
              eventResize={(arg) => {
                onResizeEvent(arg, updateEvent);
              }}
              plugins={[
                listPlugin,
                dayGridPlugin,
                timelinePlugin,
                timeGridPlugin,
                interactionPlugin,
              ]}
            />
          </StyledCalendar>
        </Card>
      </Container>

      <Dialog
        fullWidth
        maxWidth="md"
        open={openForm}
        onClose={onCloseForm}
        transitionDuration={{
          enter: theme.transitions.duration.shortest,
          exit: theme.transitions.duration.shortest - 80,
        }}
      >
        <DialogTitle sx={{ minHeight: 76 }}>
          {openForm && <> {currentEvent?.id ? t('Edit shift') : t('Add shift')}</>}
        </DialogTitle>

        <CalendarShiftForm
          currentEvent={currentEvent}
          currentShift={currentShift}
          colorOptions={CALENDAR_COLOR_OPTIONS}
          onClose={() => {
            dispatch(
              getShifts({
                fromDate: startDate,
                toDate: endDate,
                pageSize: 2 ** 31 - 1,
                pageNumber: 0,
              })
            );
            onCloseForm();
          }}
        />
      </Dialog>
    </>
  );
}
